import * as React from "react";
import combinePromises from "combine-promises";
import { useAuth } from "./auth-context";
import { useOktaAuth } from "@okta/okta-react";
import { client } from "../utils/api-client";
import { useAsync } from "../utils/asyncHooks";

import FullPageSpinner from "../screens/components/elements/FullPageSpinner";
import { FullPageErrorFallback } from "../screens/components/elements/FullPageErrorFallback";

async function getContentData(token) {
  let content = null;
  let widgets = null;
  let serverError = null;
  let logout = false;
  let customHeaders = {
    "x-mock-response-code": "200",
  };
  try {
    if (!token) {
      const contentResult = await client("content", {
        headers: customHeaders,
      });
      if (contentResult?.data) {
        return {
          content: contentResult.data,
        };
      } else {
        return {
          serverError: "Please try again later",
        };
      }
    } else {
      const { contentResult, widgetsResult } = await combinePromises({
        contentResult: client("content", { token, headers: customHeaders }),
        widgetsResult: client("widgets", { token, headers: customHeaders }),
      });

      content = contentResult?.data ? contentResult?.data : null;
      widgets = widgetsResult?.data ? widgetsResult?.data : null;
      logout =
        (!content && !serverError) || (!widgets && !serverError) ? true : false;
    }
    return { content, widgets, serverError, logout };
  } catch (err) {
    let message = err?.message ? err?.message : "something is wrong";
    return { serverError: message };
  }
}

const ContentContext = React.createContext();
ContentContext.displayName = "ContentContext";

function ContentProvider(props) {
  const { data, status, error, isLoading, isIdle, isError, isSuccess, run } =
    useAsync();

  const { logout, isLoggedIn } = useAuth();
  const { oktaAuth } = useOktaAuth();

  React.useEffect(() => {
    let contentDataPromise = isLoggedIn
      ? getContentData(oktaAuth.getAccessToken())
      : getContentData();
    run(contentDataPromise);
  }, [run, isLoggedIn, oktaAuth]);

  const value = React.useMemo(() => ({ data }), [data]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <FullPageErrorFallback error={error} />;
  }

  if (isSuccess || isIdle) {
    if (value?.data?.serverError === "logout") {
      logout();
    }
    return <ContentContext.Provider value={value} {...props} />;
  }

  throw new Error(`Unhandled status: ${status}`);
}

function useContent() {
  const context = React.useContext(ContentContext);
  if (context === undefined) {
    throw new Error(`useContent must be used within a ContentProvider`);
  }
  return context;
}

export { ContentProvider, useContent };

// const useIsMounted = () => {
//   const isMounted = React.useRef(false);
//   React.useEffect(() => {
//     isMounted.current = true;
//     return () => (isMounted.current = false);
//   }, []);
//   return isMounted;
// };
